import React from "react";
import { ClipboardList } from "lucide-react";
import ReportDataGrid from "@/components/reports/layout/ReportDataGrid";
import ReportStatCard from "@/components/reports/layout/ReportStatCard";
import {
    reportAccent,
    reportAccentGreen,
    reportBodyText,
    reportCard,
    reportHeading,
    reportSectionTitle,
    reportShell,
    reportShellBody,
} from "@/components/reports/layout/reportTheme";
import { formatReportDisplayDate, getExportBookingReportByLabel } from "@/utility/fclReportHelpers";
import { getReportDefinition, resolveReportMetrics } from "./fclReportDefinitions";

const FclReportView = ({
    reportKey,
    filters,
    summary = {},
    primaryList = [],
    sections = {},
    showReportByFilter = true,
    showKpiSummary = true,
    serverPagination = null,
}) => {
    const definition = getReportDefinition(reportKey);
    if (!definition) return null;

    const metrics = resolveReportMetrics(summary, definition.metrics);
    const fromLabel = formatReportDisplayDate(filters?.FromDate);
    const toLabel = formatReportDisplayDate(filters?.ToDate);
    const reportByLabel = showReportByFilter
        ? getExportBookingReportByLabel(filters?.ReportBy)
        : null;

    const sectionDefs = definition.sections || [];
    const sectionsAfterList = definition.sectionsAfterList === true;
    const useCompactGrid = definition.useCompactGrid !== false;

    const renderSections = () =>
        sectionDefs.map((section) => {
            const Icon = section.icon;
            const data = sections[section.id] || [];
            return (
                <div key={section.id} className={`${reportCard} overflow-hidden`}>
                    <div className={reportSectionTitle}>
                        {Icon && <Icon className={`h-5 w-5 ${reportAccent}`} />}
                        <span className={`text-sm font-semibold ${reportHeading}`}>{section.title}</span>
                    </div>
                    <ReportDataGrid
                        gridId={`${reportKey}-${section.id}`}
                        data={data}
                        columns={section.columns}
                        emptyText={`No ${section.title.toLowerCase()} for the selected filters.`}
                        defaultPageSize={useCompactGrid ? 25 : 10}
                        paginationTitle="records"
                        compact={useCompactGrid}
                        bare
                    />
                </div>
            );
        });

    const metricsGridClass =
        metrics.length >= 5
            ? "grid-cols-2 md:grid-cols-3 xl:grid-cols-5"
            : metrics.length >= 3
              ? "grid-cols-2 md:grid-cols-3 xl:grid-cols-4"
              : "grid-cols-2 md:grid-cols-2";

    return (
        <div className="space-y-3">
            {showKpiSummary && (
                <div className={`${reportShell} text-center`}>
                    <div className={`${reportShellBody} py-5`}>
                        <h2 className={`text-lg sm:text-xl font-bold ${reportHeading}`}>{definition.summaryTitle}</h2>
                        <p className={`mt-2 text-[13px] ${reportBodyText}`}>
                            From <span className={`font-semibold ${reportAccentGreen}`}>{fromLabel}</span> to{" "}
                            <span className={`font-semibold ${reportAccentGreen}`}>{toLabel}</span>
                            {reportByLabel && (
                                <>
                                    {" · "}Filter by{" "}
                                    <span className={`font-semibold ${reportAccentGreen}`}>{reportByLabel}</span>
                                </>
                            )}
                        </p>
                    </div>
                </div>
            )}

            {showKpiSummary && metrics.length > 0 && (
                <div className={`grid gap-2 sm:gap-3 ${metricsGridClass}`}>
                    {metrics.map((m) => (
                        <ReportStatCard
                            key={m.label}
                            icon={m.icon}
                            label={m.label}
                            value={m.value}
                            colorIndex={m.colorIndex}
                        />
                    ))}
                </div>
            )}

            {!sectionsAfterList && renderSections()}

            <div className={`${reportCard} overflow-hidden`}>
                {useCompactGrid && (
                    <div className={reportSectionTitle}>
                        <ClipboardList className={`h-5 w-5 ${reportAccent}`} />
                        <span className={`text-sm font-semibold ${reportHeading}`}>
                            {definition.listTitle}
                        </span>
                        <span className={`ml-auto text-xs ${reportBodyText}`}>
                            {primaryList.length} record{primaryList.length === 1 ? "" : "s"}
                        </span>
                    </div>
                )}
                <ReportDataGrid
                    gridId={`${reportKey}-primary`}
                    data={primaryList}
                    columns={definition.listColumns}
                    title={useCompactGrid ? undefined : definition.listTitle}
                    emptyText="No records found for the selected filters."
                    defaultPageSize={useCompactGrid ? 25 : 10}
                    paginationTitle="records"
                    compact={useCompactGrid}
                    bare={useCompactGrid}
                    serverPagination={serverPagination}
                />
            </div>

            {sectionsAfterList && renderSections()}
        </div>
    );
};

export default FclReportView;
